import { DateTime } from 'luxon';

/**
 * Date maths for the report's period control.
 *
 * Its own module so the page and the picker share one definition of what a
 * day is — and so the rules can be tested without rendering anything.
 */

export type ReportRange = '7d' | '30d' | '90d' | 'month' | 'custom';

export interface ReportSearch {
  /**
   * The old page's tabs. Kept so a saved `?tab=owners` link still lands on
   * the right part of the page — it now scrolls to that section.
   */
  tab?: 'overview' | 'owners' | 'outcomes' | 'followups' | 'callers';
  range?: ReportRange;
  from?: string;
  to?: string;
  /** Absent is "previous period". */
  compare?: 'none';
  /** One of our lines, E.164. */
  line?: string;
  /** One team member's user id. */
  user?: string;
  /** Absent is inbound, the report's headline scope. */
  direction?: 'outbound' | 'all';
  /** Absent is every hour. `open` = inside business hours, `closed` = after. */
  hours?: 'open' | 'closed';
}
/** A calendar day as a plain date string — no zone, because the day IS the
 *  value. The company timezone is applied later, when it becomes an instant. */
export function toDayKey(date: Date): string {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
}

export function fromDayKey(key: string | undefined): Date | undefined {
  if (!key) return undefined;
  const parsed = DateTime.fromISO(key);
  return parsed.isValid ? parsed.toJSDate() : undefined;
}

/** "3 – 17 Sep" / "28 Aug – 17 Sep 2026" — only what changes is repeated. */
export function customLabel(from?: string, to?: string): string {
  const a = fromDayKey(from);
  const b = fromDayKey(to);
  if (!a && !b) return 'Custom range';
  const start = a ? DateTime.fromJSDate(a) : null;
  const end = b ? DateTime.fromJSDate(b) : null;
  if (start && !end) return `From ${start.toFormat('d LLL yyyy')}`;
  if (!start && end) return `Until ${end.toFormat('d LLL yyyy')}`;
  if (!start || !end) return 'Custom range';
  if (start.hasSame(end, 'year')) {
    return start.hasSame(end, 'month')
      ? `${start.toFormat('d')} – ${end.toFormat('d LLL yyyy')}`
      : `${start.toFormat('d LLL')} – ${end.toFormat('d LLL yyyy')}`;
  }
  return `${start.toFormat('d LLL yyyy')} – ${end.toFormat('d LLL yyyy')}`;
}

/**
 * A named window turned into instants, in the COMPANY's timezone.
 *
 * Whole days, like the queue's own picker: "30 Sep" means through 11:59pm on
 * the 30th where the business is, not where the reader is. Resolving it in
 * the viewer's zone would shift the boundary — a Pacific reader asking an
 * Eastern tenant for September would lose its first three hours.
 */
export function rangeToInstants(
  range: ReportRange | undefined,
  search: ReportSearch,
  timeZone: string | undefined,
): { startDate?: string; endDate?: string } {
  const zone = timeZone ? { zone: timeZone } : {};
  const now = DateTime.now().setZone(timeZone ?? 'local');

  if (range === 'custom' || (!range && (search.from || search.to))) {
    const from = search.from ? DateTime.fromISO(search.from, zone).startOf('day') : null;
    const to = search.to ? DateTime.fromISO(search.to, zone).endOf('day') : null;
    return {
      startDate: from?.isValid ? (from.toUTC().toISO() ?? undefined) : undefined,
      endDate: to?.isValid ? (to.toUTC().toISO() ?? undefined) : undefined,
    };
  }

  const start =
    range === 'month'
      ? now.startOf('month')
      : now.minus({ days: range === '7d' ? 6 : range === '90d' ? 89 : 29 }).startOf('day');

  return {
    startDate: start.toUTC().toISO() ?? undefined,
    endDate: now.endOf('day').toUTC().toISO() ?? undefined,
  };
}


/**
 * The range as whole local days — what the report endpoint takes. The server
 * applies the company timezone; these are calendar days, not instants.
 */
export function rangeToDays(
  range: ReportRange | undefined,
  search: ReportSearch,
  timeZone: string | undefined,
): { from: string; to: string } {
  const today = DateTime.now().setZone(timeZone ?? 'local').startOf('day');
  if (range === 'custom' || (!range && (search.from || search.to))) {
    const from = search.from && DateTime.fromISO(search.from).isValid ? search.from : null;
    const to = search.to && DateTime.fromISO(search.to).isValid ? search.to : null;
    const end = to ?? today.toISODate()!;
    return { from: from ?? DateTime.fromISO(end).minus({ days: 29 }).toISODate()!, to: end };
  }
  const start =
    range === 'month'
      ? today.startOf('month')
      : today.minus({ days: range === '7d' ? 6 : range === '90d' ? 89 : 29 });
  return { from: start.toISODate()!, to: today.toISODate()! };
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The route's `validateSearch`: whitelisted, so a bad URL narrows to nothing. */
export function parseReportSearch(search: Record<string, unknown>): ReportSearch {
  const out: ReportSearch = {};
  // The old page's tabs are sections now. Kept, so a saved `?tab=owners`
  // still scrolls to the people table; overview is the top, so it is dropped.
  const tab = search.tab;
  if (tab === 'owners' || tab === 'outcomes' || tab === 'followups' || tab === 'callers') {
    out.tab = tab;
  }
  const range = search.range;
  if (range === '7d' || range === '30d' || range === '90d' || range === 'month' || range === 'custom') {
    out.range = range;
  }
  if (typeof search.from === 'string' && DAY.test(search.from)) out.from = search.from;
  if (typeof search.to === 'string' && DAY.test(search.to)) out.to = search.to;
  if (search.compare === 'none') out.compare = 'none';
  if (typeof search.line === 'string' && /^\+?\d{7,15}$/.test(search.line)) out.line = search.line;
  if (typeof search.user === 'string' && /^[\w-]{1,64}$/.test(search.user)) out.user = search.user;
  if (search.direction === 'outbound' || search.direction === 'all') out.direction = search.direction;
  if (search.hours === 'open' || search.hours === 'closed') out.hours = search.hours;
  // `reason` and `owner` belonged to the queue report and are dropped: an
  // old link lands on the whole report rather than on a 400.
  return out;
}
